import { Alert } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { ContactCard } from "./components/ContactCard.tsx"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { PortalSection } from "./components/PortalSection.tsx"
import { ProjectContextBanner } from "./components/ProjectContextBanner.tsx"
import { resolveGcContact } from "./helpers/recipient.ts"

/** No messaging backend exists anywhere in the platform yet (confirmed
 * across every prior audit of this codebase) — this is the honest working
 * alternative rather than a fake conversation UI or a dead disabled
 * button: the customer's real assigned contact(s), reachable by email or
 * phone right now. Global page — see the same `?project=`-from-the-URL
 * note on `PortalDocumentsPage`. */
function PortalMessagesPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get("project")
  const projects = useAppSelector((state) => state.portal.projects)

  const visibleProjects = useMemo(
    () => (projectId ? projects.filter((p) => p.id === projectId) : projects),
    [projects, projectId]
  )

  return (
    <PortalPageContainer className="max-w-3xl">
      <PortalPageHeader title={t("portal.messages.title")} />

      {projectId && visibleProjects[0] && (
        <ProjectContextBanner
          projectId={projectId}
          projectName={visibleProjects[0].lead_name}
          clearHref={RoutesPaths.portal.messages}
        />
      )}

      <Alert
        className="mb-5"
        type="info"
        showIcon
        message={t("portal.messages.explanation")}
      />

      {visibleProjects.length === 0 ? (
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {visibleProjects.map((project) => (
            <PortalSection key={project.id} title={project.lead_name}>
              <ContactCard contact={resolveGcContact(project.project_managers) ?? null} />
            </PortalSection>
          ))}
        </div>
      )}
    </PortalPageContainer>
  )
}

export default PortalMessagesPage
